/**
 * SIED process step ("Suivi") for a case — labels and colours (T-100).
 *
 * The NestJS SIED poller reads each form's Suivi off SIED's search page and
 * stores it on sied_records; funerals_searchable exposes it as sied_status /
 * sied_status_raw / sied_status_checked_at / sied_status_changed_at. Values and
 * mapping live in packages/nestjs/src/sied/sied-status.ts.
 *
 * Our step is the last one (funeral representative signs). SIED lets us sign
 * once the clinical certifier has signed, validated or not — so both
 * certifier_* states are "our turn" (blue). Not signed by the doctor is amber:
 * the SP-3 exists but there's nothing for us to sign yet.
 */

export type SiedStatus =
  | 'not_signed_by_physician'
  | 'certifier_signed_not_validated'
  | 'certifier_signed_validated'
  | 'funeral_rep_signed'
  | 'cancelled'
  | 'unknown';

type Lang = 'en' | 'fr';

interface SiedStatusDisplay {
  /** Compact text for the list column. */
  short: Record<Lang, string>;
  /** Full sentence for the sidebar / tooltip. FR is SIED's own wording. */
  long: Record<Lang, string>;
  /** Tailwind classes for the pill. */
  cls: string;
}

export const SIED_STATUS_DISPLAY: Record<SiedStatus, SiedStatusDisplay> = {
  not_signed_by_physician: {
    short: { en: 'Not signed by doctor', fr: 'Non signé (médecin)' },
    long: {
      en: 'SP-3 received, not signed by doctor',
      fr: 'Non signé par le signataire clinique',
    },
    cls: 'border-amber-500 bg-amber-100 text-amber-800',
  },
  certifier_signed_not_validated: {
    short: { en: 'Certifier signed', fr: 'Signé (clinique)' },
    long: {
      en: 'Signed by clinical certifier, but not validated',
      fr: 'Signé par le signataire clinique, mais non validé',
    },
    cls: 'border-blue-400 bg-blue-50 text-blue-700',
  },
  certifier_signed_validated: {
    short: { en: 'Certifier validated', fr: 'Validé (clinique)' },
    long: {
      en: 'Signed by clinical certifier and validated',
      fr: 'Signé par le signataire clinique et validé',
    },
    cls: 'border-blue-500 bg-blue-100 text-blue-800',
  },
  funeral_rep_signed: {
    short: { en: 'Signed by funeral rep', fr: 'Signé (funéraire)' },
    long: {
      en: 'Signed by funeral representative',
      fr: 'Signé par le signataire funéraire',
    },
    cls: 'border-green-600 bg-green-50 text-green-700',
  },
  cancelled: {
    short: { en: 'Cancelled', fr: 'Annulé' },
    long: { en: 'Form cancelled in SIED', fr: 'Bulletin annulé' },
    cls: 'border-gray-300 bg-gray-100 text-gray-500 line-through',
  },
  unknown: {
    short: { en: 'Unknown', fr: 'Inconnu' },
    long: { en: 'SIED shows a status we don’t recognise', fr: 'Statut SIED non reconnu' },
    cls: 'border-gray-300 bg-gray-50 text-gray-600',
  },
};

export const langOf = (locale: string | undefined): Lang =>
  (locale ?? '').toLowerCase().startsWith('fr') ? 'fr' : 'en';

export function siedDisplay(status: string | null | undefined): SiedStatusDisplay | null {
  if (!status) return null;
  return SIED_STATUS_DISPLAY[status as SiedStatus] ?? SIED_STATUS_DISPLAY.unknown;
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" (EN) or "à l'instant" / "il y a 5 min"… */
export function siedAgo(iso: string | null | undefined, lang: Lang, now = Date.now()): string {
  if (!iso) return '';
  const mins = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins)) return '';
  const [n, unit] =
    mins < 1 ? [0, ''] : mins < 60 ? [mins, 'm'] : mins < 1440 ? [Math.floor(mins / 60), 'h'] : [Math.floor(mins / 1440), 'd'];
  if (lang === 'fr') {
    if (!unit) return 'à l’instant';
    return `il y a ${n} ${unit === 'm' ? 'min' : unit === 'h' ? 'h' : 'j'}`;
  }
  return unit ? `${n}${unit} ago` : 'just now';
}

export const SIED_LABELS = {
  column: { en: 'SIED', fr: 'SIED' },
  checked: { en: 'checked', fr: 'vérifié' },
  since: { en: 'since', fr: 'depuis' },
  never: { en: 'Not checked yet', fr: 'Pas encore vérifié' },
  refresh: { en: 'Refresh SIED status', fr: 'Actualiser le statut SIED' },
  refreshing: { en: 'Checking SIED…', fr: 'Vérification SIED…' },
  refreshFailed: { en: 'SIED check failed', fr: 'Échec de la vérification SIED' },
} as const;
